import React, { useContext, useMemo } from 'react';
import { DoubleRightOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { Empty, Modal } from '@maxtropy/components';
import { apiV2EnvScreenAreaDataPost, type V2EnvScreenAreaDataPostResponse } from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { isEmpty } from 'lodash-es';
import { keepTwoAndWanOrLine } from '@/utils/utils';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
import iconVocs from '../../imgs/icon-vocs@2x.png';
import iconInlet from '../../imgs/icon-inlet@2x.png';
import styles from './index.module.scss';

interface AreaDataModalProps {
  open: boolean;
  areaName?: string;
  onClose: () => void;
}

type AreaItem = NonNullable<V2EnvScreenAreaDataPostResponse['list']>[number];
type DeviceItem = NonNullable<AreaItem['devices']>[number];

const CATEGORY_TITLE: Record<number, string> = {
  1: 'VOCS出口数据',
  2: '废水环保监测数据',
};

const CATEGORY_ICON: Record<number, string> = {
  1: iconVocs,
  2: iconInlet,
};

const formatValue = (value?: number | null, unit?: string) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  return `${keepTwoAndWanOrLine(value)}${unit ?? ''}`;
};

const AreaDataModal: React.FC<AreaDataModalProps> = ({ open, areaName, onClose }) => {
  const { isFullScreen, scale } = useContext(ShareDataWrapperContext);
  const normalizedAreaName = areaName?.trim();

  const { data, loading } = useRequest(
    () =>
      apiV2EnvScreenAreaDataPost({
        zoneAlias: normalizedAreaName,
      } as any),
    {
      ready: open && !!normalizedAreaName,
      refreshDeps: [open, normalizedAreaName],
    }
  );

  const areaData = data?.list?.[0];
  const devices = useMemo(() => areaData?.devices ?? [], [areaData?.devices]);

  const renderDeviceCard = (device: DeviceItem, index: number) => {
    const category = areaData?.deviceCategory ?? 0;
    const title = CATEGORY_TITLE[category] ?? device.deviceName ?? '监测数据';
    const titleIcon = CATEGORY_ICON[category] ?? iconVocs;
    const indicators = device.indicators ?? [];

    return (
      <section className={styles.dataCard} key={`${device.deviceId ?? device.deviceName ?? index}`}>
        <div className={styles.cardTitle}>
          <span className={styles.cardTitleIconWrap}>
            <img src={titleIcon} alt="" className={styles.cardTitleIcon} />
          </span>
          <span className={styles.cardTitleText}>{title}</span>
          {/* <DoubleRightOutlined className={styles.cardArrow} /> */}
        </div>
        <div className={styles.metricList}>
          {indicators.map((indicator, indicatorIndex) => (
            <div
              className={styles.metricItem}
              key={`${indicator.propertyId ?? indicator.propertyName ?? indicatorIndex}`}
            >
              <span className={styles.metricName}>{indicator.propertyName ?? '--'}:</span>
              <span className={styles.metricValue}>{formatValue(indicator.value, indicator.unit)}</span>
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <Modal
      title={normalizedAreaName || '区域环保数据'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={devices.length > 1 ? 900 : 480}
      mask={false}
      destroyOnClose
      centered={!isFullScreen}
      className={styles.areaDataModal}
      getContainer={() => document.getElementById('full_screen_box') ?? document.body}
    >
      <Spin spinning={loading}>
        {isEmpty(devices) ? (
          <Empty style={{ height: 260, paddingTop: 82 }} />
        ) : (
          <div className={styles.cardGrid}>{devices.map(renderDeviceCard)}</div>
        )}
      </Spin>
    </Modal>
  );
};

export default AreaDataModal;
